/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { StandardCard } from "../../../components/Cards";
import { useDispatchWrapper } from "../../../hooks";
import { formatText } from "../../../helpers";
import { Leads } from "../../leads/Leads";
import { ChatLeads } from "../../chatLeads/Chatleads";
import { fetchLeadStatusCountAsync } from "../../../redux/leads/leadsActions";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../redux/initialStoreState";
import { LoadState } from "../../../constants/enums";
import { useDateFilter } from "../../../components/Table/hooks/useDateFilter";
import { useBillingCompanyContext } from "../../../providers";
import { fetchWhatsappStatusCountAsync } from "../../../redux/activity/activityActions";
import { useNavigate } from "react-router-dom";
import { EditCustomerRightPanel } from "../../customers/EditCustomer";
import { clearStudent } from "../../../redux/student/studentActions";
import { FeaturedCarousel } from "./FeaturedCarousel";
import { api } from "../../../api/api";
import { NoteAdd, PersonAdd } from "@mui/icons-material";

export const Counts: React.FC<{ hideCarousel?: boolean }> = ({
  hideCarousel = false,
}) => {
  const dispatch = useDispatchWrapper();
  const [openByParam, setOpenByParam] = React.useState<string>("");
  const [openEditCustomer, setOpenEditCustomer] =
    React.useState<Boolean>(false);

  const [chatExternalCounts, setChatExternalCounts] = React.useState<
    Record<string, number>
  >({});
  const [chatExternalLoading, setChatExternalLoading] =
    React.useState<boolean>(false);

  const { list: recordsCounts, loading } = useSelector(
    (storeState: IStoreState) => storeState.leads.recordCounts,
  );

  const { list: recordsCountsWhatsapp, loading: loadingWhatsapp } = useSelector(
    (storeState: IStoreState) => storeState.acitivty.whatsappStatusCounts,
  );



  const { date } = useDateFilter({ defaultType: "today" });

  const companyOutlet = useBillingCompanyContext();
  const navigate = useNavigate();
  const dateState = React.useMemo(() => ({ dates: date }), [date]);

  const [monthType, setMonthType] = React.useState<string>("Enquiry_Month");

  React.useEffect(() => {
    if (companyOutlet.comapny.company_outlet_uuid) {
      dispatch(
        fetchLeadStatusCountAsync(companyOutlet.comapny.company_outlet_uuid),
      );
    }
  }, [date, dispatch, companyOutlet.comapny.company_outlet_uuid]);



  React.useEffect(() => {
    const fetchChatExternalCounts = async () => {
      try {
        setChatExternalLoading(true);
        const res = await api.get("/leads/get-status-chat-external-leads", {
          params: {
            type: "count",
            company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
          },
        });

        setChatExternalCounts(res.data?.data?.statusCounts || {});
      } catch (err) {
        setChatExternalCounts({});
      } finally {
        setChatExternalLoading(false);
      }
    };

    if (companyOutlet.comapny.company_outlet_uuid) {
      fetchChatExternalCounts();
    }
  }, [date, companyOutlet.comapny.company_outlet_uuid]);

  const isExternalLeadFollowupsStatus = React.useCallback((status: string) => {
    return (
      status === "TODAYS_NEW_EXTERNAL_LEADS" ||
      status === "EXTERNAL_LEAD_MISSED_FOLLOWUPS" ||
      status === "EXTERNAL_LEAD_TODAY_FOLLOWUPS"
    );
  }, []);

  const normalizeExternalCountKey = React.useCallback((status: string) => {
    // API returns EXTERNAL_LEAD_TODAY_FOLLOWUPS (no "S"), while UI status is EXTERNAL_LEAD_TODAY_FOLLOWUPS
    if (status === "EXTERNAL_LEAD_TODAY_FOLLOWUPS") {
      return "EXTERNAL_LEAD_TODAY_FOLLOWUPS";
    }
    return status;
  }, []);

  const actionButtons = (
    <Stack
      direction="row"
      spacing={{ xs: 1, sm: 2 }}
      justifyContent="flex-end"
      sx={{ width: "100%", maxWidth: "100%" }}
    >
      <Button
        variant="contained"
        color="success"
        startIcon={<NoteAdd />}
        sx={{
          background: "#027302",
          fontWeight: 600,
          fontSize: { xs: "0.7rem", sm: "0.875rem" },
          padding: { xs: "4px 8px", sm: "6px 16px" },
          minWidth: { xs: "auto", sm: "64px" },
          whiteSpace: "nowrap",
        }}
        onClick={() => {
          navigate("/event-leads/manage");
        }}
      >
        Create New Lead
      </Button>
      <Button
        variant="contained"
        startIcon={<PersonAdd />}
        sx={{
          fontSize: { xs: "0.7rem", sm: "0.875rem" },
          padding: { xs: "4px 8px", sm: "6px 16px" },
          minWidth: { xs: "auto", sm: "64px" },
          whiteSpace: "nowrap",
        }}
        onClick={() => {
          setOpenEditCustomer(true);
        }}
      >
        Add Guests
      </Button>
    </Stack>
  );

  return (
    <>
      {/* Desktop: label + buttons in one row */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        mb={1}
        sx={{ display: { xs: "none", md: "flex" } }}
      >
        <Typography
          variant="h3"
          fontWeight={700}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Leads Count
        </Typography>
        {actionButtons}
      </Stack>

      {/* Mobile: only buttons at top (right-aligned); label moves below banner */}
      <Box sx={{ display: { xs: "block", md: "none" }, mb: 1 }}>
        {actionButtons}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "stretch",
          gap: 1,
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: "50%", md: "66.66%" },
            order: { xs: 2, sm: 1 },
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Mobile: Leads Count label appears here, after banner */}
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{ display: { xs: "block", md: "none" }, mb: 1 }}
          >
            Leads Count
          </Typography>
          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr) minmax(0, 1fr)",
                md: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
              },
              gridAutoRows: "1fr",
              gap: 1,
              width: "100%",
            }}
          >
            {[
              "ONGOING",
              "MISSED_FOLLOWUPS",
              "TODAY_FOLLOWUPS",
              "TODAYS_NEW_EXTERNAL_LEADS",
              "EXTERNAL_LEAD_MISSED_FOLLOWUPS",
              "EXTERNAL_LEAD_TODAY_FOLLOWUPS",
              // "TODAY_MISSED_FOLLOWUPS",
            ].map((status: any) => {
              const count = (() => {
                if (isExternalLeadFollowupsStatus(String(status))) {
                  const key = normalizeExternalCountKey(String(status));
                  return chatExternalCounts?.[key] || 0;
                }

                return recordsCounts?.[status] || 0;
              })();

              return (
                <Box
                  key={status}
                  onClick={() => {
                    setOpenByParam(status);
                  }}
                >
                  <TaskCountCard
                    label={
                      ["ONGOING", "MISSED_FOLLOWUPS", "TODAY_FOLLOWUPS"].includes(status)
                        ? `Event Leads ${formatText(status)}`
                        : formatText(status)
                    }
                    count={Number(count)}
                    loading={
                      isExternalLeadFollowupsStatus(String(status))
                        ? chatExternalLoading
                        : loading === LoadState.InProgress
                    }
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
        {!hideCarousel && (
          <Box
            sx={{
              width: { xs: "100%", sm: "50%", md: "33.33%" },
              order: { xs: 1, sm: 2 },
              minWidth: 0,
              alignSelf: "flex-start",
            }}
          >
            <FeaturedCarousel />
          </Box>
        )}
      </Box>
      {openByParam && (
        <Box sx={{ mt: 2 }}>
          {isExternalLeadFollowupsStatus(openByParam) ? (
            <ChatLeads statusParam={openByParam} hideFilters />
          ) : (
            <Leads
              dashboardConfig={{
                statusParam: openByParam,
                monthType,
                dateState,
              }}
            />
          )}
        </Box>
      )}
      {openEditCustomer && (
        <EditCustomerRightPanel
          open={true}
          onClose={() => {
            setOpenEditCustomer(false);
            dispatch(clearStudent());
          }}
          onComplete={() => {
            setOpenEditCustomer(false);
            dispatch(clearStudent());
          }}
        />
      )}
    </>
  );
};

export const TaskCountCard: React.FC<{
  label: string;
  count: number;
  loading?: boolean;
}> = (props) => {
  const isDekstop = useMediaQuery((theme: any) => theme.breakpoints.up("sm"));

  return (
    <Card
      sx={{
        m: 0,
        p: 1.5,
        height: "100%",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        borderRadius: 3,
        transition: "all 0.3s ease",
        background: "linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)",
        border: "none",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #0D3B66, #a1c2e2ff)",
          opacity: 0,
          transition: "opacity 0.3s ease",
        },
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 30px rgba(63, 60, 251, 0.2)",
          "&::before": {
            opacity: 1,
          },
        },
      }}
    >
      {isDekstop ? (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Typography
            variant="h5"
            sx={{
              textTransform: "uppercase",
              color: "#414141ff",
              fontWeight: 700,
              display: "block",
              mb: 0.5,
            }}
          >
            {props.loading ? <Skeleton width={150} height={24} /> : props.label}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: "2rem",
              color: "#1f2937",
              lineHeight: 1,
            }}
          >
            {props.loading ? <Skeleton width={60} height={40} /> : props.count}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Typography
            variant="h5"
            fontWeight={500}
            sx={{
              marginBottom: "8px",
              textAlign: "center",
              lineHeight: 1.2,
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
            }}
          >
            {props.loading ? <Skeleton width={60} height={28} /> : props.count}
          </Typography>
          <Typography
            variant="caption"
            fontWeight={400}
            sx={{
              textAlign: "center",
              lineHeight: 1.2,
              fontSize: { xs: "0.6rem", sm: "0.7rem" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {props.loading ? <Skeleton width={120} height={16} /> : props.label}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export const WhatsappTaskCountCard: React.FC<{
  label: string;
  whatsappCount: number;
  loadingWhatsapp?: boolean;
}> = (props) => {
  const isDekstop = useMediaQuery((theme: any) => theme.breakpoints.up("sm"));

  return (
    <Card
      sx={{
        m: 0,
        height: "100%",
        borderRadius: 3,
        transition: "all 0.3s ease",
        background: "linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)",
        border: "none",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #0D3B66, #a1c2e2ff)",
          opacity: 0,
          transition: "opacity 0.3s ease",
        },
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 30px rgba(63, 60, 251, 0.2)",
          "&::before": {
            opacity: 1,
          },
        },
      }}
    >
      {isDekstop ? (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Typography
            variant="h4"
            sx={{
              textTransform: "uppercase",
              color: "#414141ff",
              fontWeight: 700,
              display: "block",
              mb: 0.5,
            }}
          >
            {props.loadingWhatsapp ? (
              <Skeleton width={150} height={24} />
            ) : (
              props.label
            )}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: "2rem",
              color: "#1f2937",
              lineHeight: 1,
            }}
          >
            {props.loadingWhatsapp ? (
              <Skeleton width={60} height={40} />
            ) : (
              props.whatsappCount
            )}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Typography
            variant="h5"
            fontWeight={500}
            sx={{
              marginBottom: "8px",
              textAlign: "center",
              lineHeight: 1.2,
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
            }}
          >
            {props.loadingWhatsapp ? (
              <Skeleton width={60} height={28} />
            ) : (
              props.whatsappCount
            )}
          </Typography>
          <Typography
            variant="caption"
            fontWeight={400}
            sx={{
              textAlign: "center",
              lineHeight: 1.2,
              fontSize: { xs: "0.6rem", sm: "0.7rem" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {props.loadingWhatsapp ? (
              <Skeleton width={120} height={16} />
            ) : (
              props.label
            )}
          </Typography>
        </Box>
      )}
    </Card>
  );
};
